// Gestionnaire de consentement cookies conforme RGPD/CNIL

const CONSENT_KEY = 'tresopilot_cookie_consent';
const ANONYMOUS_ID_KEY = 'tresopilot_anonymous_id';
const CONSENT_VERSION = '1.0';
const CONSENT_DURATION_MONTHS = 6;

// Générer un UUID simple
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Obtenir ou créer un ID anonyme
export function getOrCreateAnonymousId() {
  if (typeof window === 'undefined') return null;
  
  let anonymousId = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!anonymousId) {
    anonymousId = generateUUID();
    localStorage.setItem(ANONYMOUS_ID_KEY, anonymousId);
  }
  return anonymousId;
}

// Obtenir le consentement stocké localement
export function getLocalConsent() {
  if (typeof window === 'undefined') return null;
  
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;
    
    const consent = JSON.parse(stored);
    
    // Vérifier l'expiration
    if (consent.expires_at && new Date(consent.expires_at) < new Date()) {
      localStorage.removeItem(CONSENT_KEY);
  try {
    window.dispatchEvent(new CustomEvent('tresopilot:consent_updated', { detail: null }));
  } catch (_) {}

      return null;
    }
    
    return consent;
  } catch (error) {
    console.error('Error reading consent:', error);
    return null;
  }
}

// Sauvegarder le consentement localement
export function saveLocalConsent(preferences, meta = {}) {
  if (typeof window === 'undefined') return;

  // Allow restoring exact expiry/version from DB (cross-device sync)
  let expiresIso = meta?.expires_at;
  if (!expiresIso) {
    const expiresAt = new Date();
    expiresAt.setMonth(expiresAt.getMonth() + CONSENT_DURATION_MONTHS);
    expiresIso = expiresAt.toISOString();
  }

  const consent = {
    ...preferences,
    consent_version: meta?.consent_version || CONSENT_VERSION,
    expires_at: expiresIso,
    updated_at: meta?.updated_at || new Date().toISOString(),
  };

  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));

  // Notify the app that consent changed (for gated trackers)
  try {
    window.dispatchEvent(new CustomEvent('tresopilot:consent_updated', { detail: consent }));
  } catch (_) {}

}

// Effacer le consentement local
export function clearLocalConsent() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(CONSENT_KEY);
  try {
    window.dispatchEvent(new CustomEvent('tresopilot:consent_updated', { detail: null }));
  } catch (_) {}

}

// Vérifier si on peut utiliser les analytics
export function canUseAnalytics() {
  const consent = getLocalConsent();
  return consent?.analytics === true;
}

// Vérifier si on peut utiliser le marketing
export function canUseMarketing() {
  const consent = getLocalConsent();
  return consent?.marketing === true;
}

// Vérifier si l'utilisateur a fait un choix
export function hasUserConsented() {
  return getLocalConsent() !== null;
}

// Obtenir les préférences complètes
export function getConsentPreferences() {
  const consent = getLocalConsent();
  if (!consent) {
    return {
      necessary: true,
      analytics: false,
      marketing: false
    };
  }
  return {
    necessary: true, // Toujours true
    analytics: consent.analytics || false,
    marketing: consent.marketing || false
  };
}

// Créer l'objet de consentement pour la DB
export function createConsentPayload(preferences, userId = null) {
  const expiresAt = new Date();
  expiresAt.setMonth(expiresAt.getMonth() + CONSENT_DURATION_MONTHS);
  
  let consentStatus = 'custom';
  if (preferences.analytics && preferences.marketing) {
    consentStatus = 'accepted_all';
  } else if (!preferences.analytics && !preferences.marketing) {
    consentStatus = 'rejected_all';
  }
  
  return {
    user_id: userId,
    anonymous_id: getOrCreateAnonymousId(),
    necessary: true,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
    consent_status: consentStatus,
    consent_version: CONSENT_VERSION,
    expires_at: expiresAt.toISOString(),
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null
  };
}

// Charger les scripts analytics si autorisé
export function loadAnalyticsScripts() {
  if (!canUseAnalytics()) return;
  
  // PLACEHOLDER: Charger vos scripts analytics ici
  // Exemple Google Analytics:
  // window.dataLayer = window.dataLayer || [];
  // function gtag(){dataLayer.push(arguments);}
  // gtag('js', new Date());
  // gtag('config', 'GA_MEASUREMENT_ID');
  
  console.log('[Consent] Analytics scripts loaded');
}

// Charger les scripts marketing si autorisé
export function loadMarketingScripts() {
  if (!canUseMarketing()) return;
  
  // PLACEHOLDER: Charger vos scripts marketing ici
  // Exemple Facebook Pixel, LinkedIn Insight Tag, etc.
  
  console.log('[Consent] Marketing scripts loaded');
}

// Initialiser les traceurs selon le consentement
export function initializeTrackers() {
  if (canUseAnalytics()) {
    loadAnalyticsScripts();
  }
  if (canUseMarketing()) {
    loadMarketingScripts();
  }
}